/**
 * Horizontal displacement of the hip relative to the ankle, ASIS/PSIS optional
 *
 * Heuristic sagittal classifier aligned with plumb-line / VGL thinking: Swayback is suggested when
 * the hip (greater trochanter proxy) sits clearly anterior to the ankle vertical with a posterior
 * ribcage shift and/or knee flexion (compensatory); Anterior Pelvic Tilt when pelvic tilt is anterior (ASIS–PSIS
 * if pinned, else thigh-line estimate from serialized metrics), shoulders drift forward of the hip,
 * and the hip stack is less “forward of ankle” than swayback thresholds. Works from landmarks when
 * provided; otherwise falls back to the same quantities parsed from lateral metric strings.
 */

import { IDX } from '@/components/posture-analysis/constants';
import type { Landmark } from '@/components/posture-analysis/types';
import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import { pelvicTiltDegFromLineAndSide } from './posture-strategy-context';
import {
  buildOrientationUiMappedInputs,
  HIP_APT_POSSIBLE_SIGNALS,
  HIP_DISPLACEMENT_SIGNAL_UI_MAP,
  HIP_SWAYBACK_POSSIBLE_SIGNALS,
  logOrientationDecisionDebug,
  logPostureClassificationDebug,
} from './posture-classification-debug';

const STRATEGY_ID = 'hipDisplacementHorizontal';
const DEBUG_LOG_PREFIX = '[hipDisplacementHorizontal]';
const STRATEGY_NAME = 'Horizontal Displacement of the hip relative to the ankle, asis/psis optional ';

/** Max points per competing orientation (used as confidence denominator). */
export const MAX_ORIENTATION_POINTS = 5;

const rad2deg = (r: number) => (r * 180) / Math.PI;

/** Same convention as `analyzeLateral` (internal angle at knee H–K–A, degrees). */
function angleAtLandmark(a: Landmark, b: Landmark, c: Landmark): number {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y);
  const mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
}

export function sagittalFromLandmarks(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext,
  kneeFlexionDegMin: number,
  kneeHyperextensionDegMin: number
): {
  hipFwdCm: number;
  shoFwdCm: number;
  torsoOffsetCm: number;
  kneeHyperext: boolean;
  kneeFlex: boolean;
  /** When `kneeFlex` is true, magnitude (deg) used for gating (evidence). */
  kneeFlexEvidenceDeg?: number;
  pelvicTilt: number;
  /** Knee-Hip Offset, anterior-positive cm (knee anterior to hip > 0). Mirrors analyzeLateral sign. */
  kneeHipAntCm: number;
} | null {
  const s = input.sagittal;
  if (!s?.landmarks?.length) return null;
  const side = s.sideView;
  const idxLR = (l: number, r: number) => (side === 'Left' ? l : r);
  const S = s.landmarks[idxLR(IDX.L_SHO, IDX.R_SHO)];
  const H = s.landmarks[idxLR(IDX.L_HIP, IDX.R_HIP)];
  const K = s.landmarks[idxLR(IDX.L_KNEE, IDX.R_KNEE)];
  const A = s.landmarks[idxLR(IDX.L_ANK, IDX.R_ANK)];
  if (!H || !A) return null;

  const plumbX = typeof s.plumbLineX === 'number' && isFinite(s.plumbLineX) ? s.plumbLineX : A.x;
  const cm = s.cmPerPx;

  const hipShift = (H.x - plumbX) * cm;
  const hipFwdCm = side === 'Left' ? (hipShift <= 0 ? Math.abs(hipShift) : 0) : hipShift >= 0 ? Math.abs(hipShift) : 0;

  const shoShift = S ? (S.x - plumbX) * cm : NaN;
  const shoFwdCm =
    S
      ? side === 'Left'
        ? shoShift <= 0
          ? Math.abs(shoShift)
          : 0
        : shoShift >= 0
          ? Math.abs(shoShift)
          : 0
      : NaN;

  const shoFwdSigned = S ? (side === 'Left' ? -shoShift : shoShift) : NaN;
  const hipFwdSigned = side === 'Left' ? -hipShift : hipShift;
  const torsoOffsetCm =
    isFinite(shoFwdSigned) && isFinite(hipFwdSigned) ? shoFwdSigned - hipFwdSigned : NaN;

  let kneeHyperext = false;
  let kneeFlex = false;
  let kneeFlexEvidenceDeg: number | undefined;
  if (H && K && A && Math.abs(A.y - H.y) > 10) {
    const kneeRawAngle = angleAtLandmark(H, K, A);
    const kneeDeg = isFinite(kneeRawAngle) ? Math.abs(180 - kneeRawAngle) : NaN;
    // Determine flex vs hyperext direction by comparing the knee to the HIP directly.
    // Using the hip-to-ankle interpolated plumb is incorrect when the hip is significantly
    // anterior to the ankle (APT/Swayback pattern): the angled plumb causes a genuinely
    // posterior (hyperextended) knee to appear anterior to the plumb reference, silently
    // setting kneeHyperext=false and preventing the Swayback-exclusion veto.
    //
    // Anatomically: flex = knee protrudes ANTERIOR to hip; hyperext = knee protrudes POSTERIOR.
    // For Right view (person faces right, anterior=larger x): flex if K.x > H.x.
    // For Left  view (person faces left,  anterior=smaller x): flex if K.x < H.x.
    const diffX = K.x - H.x;
    const directionFlex = side === 'Left' ? diffX < 0 : diffX > 0;
    if (isFinite(kneeDeg)) {
      const roundedKneeDeg = Math.round(kneeDeg);
      if (directionFlex) {
        kneeFlex = roundedKneeDeg > kneeFlexionDegMin || kneeDeg > kneeFlexionDegMin;
        if (kneeFlex) kneeFlexEvidenceDeg = kneeDeg;
      } else {
        kneeHyperext = roundedKneeDeg >= kneeHyperextensionDegMin;
      }
    }
  }

  let pelvicTilt = NaN;
  if (context.asisPsisLine) {
    pelvicTilt = pelvicTiltDegFromLineAndSide(context.asisPsisLine, side);
  } else if (s.custom?.ASIS && s.custom?.PSIS) {
    pelvicTilt = pelvicTiltDegFromLineAndSide({ a: s.custom.ASIS, b: s.custom.PSIS }, side);
  }

  if (!isFinite(pelvicTilt) && H && K && A) {
    const thighDX = K.x - H.x;
    const thighDY = K.y - H.y;
    if (Math.abs(thighDY) > 1) {
      let est = rad2deg(Math.atan2(thighDX, Math.abs(thighDY)));
      est = side === 'Left' ? -est : est;
      pelvicTilt = est;
    }
  }

  // Knee-Hip Offset, anterior-positive (knee anterior to hip > 0). Mirrors the sign in analyzeLateral,
  // whose displayed direction is 'Ant' when (side==='Left'?dx:-dx) < 0 (dx = K.x - H.x).
  const kneeHipAntCm = K ? (side === 'Left' ? -(K.x - H.x) : K.x - H.x) * cm : NaN;

  return {
    hipFwdCm,
    shoFwdCm: isFinite(shoFwdCm) ? shoFwdCm : 0,
    torsoOffsetCm: isFinite(torsoOffsetCm) ? torsoOffsetCm : 0,
    kneeHyperext,
    kneeFlex,
    ...(kneeFlexEvidenceDeg !== undefined ? { kneeFlexEvidenceDeg } : {}),
    pelvicTilt,
    kneeHipAntCm,
  };
}

export type OrientationScore = {
  pts: number;
  evidence: string[];
  /** Signal keys that evaluated true (+pts) for debug tracing */
  activeSignals: string[];
};

export function scoreSwayback(
  hipFwdCm: number,
  torsoOffsetCm: number,
  kneeFlex: boolean,
  pelvicTilt: number,
  rules: any,
  kneeFlexEvidenceDeg?: number,
  kneeHipAntCm?: number,
  /**
   * Sex-resolved pelvic tilt threshold (°) for the "anterior pelvic tilt" classification.
   * When set and `pelvicTilt ≥ pelvicTiltDegMin`, knee flexion is treated as *compensatory*
   * (APT with posterior pelvic compression) rather than a primary swayback indicator, and
   * the +2 knee-flexion / +1 knee-hip-anterior signals are suppressed.
   */
  pelvicTiltDegMin?: number
): OrientationScore {
  const ev: string[] = [];
  const activeSignals: string[] = [];
  let pts = 0;
  const { hipForwardCmMin, strictHip, trunkLeanLatDegMin, strictLean, strictTilt } = rules;

  if (hipFwdCm > hipForwardCmMin) {
    pts += 2;
    activeSignals.push('sb.hipFwd.markedAnterior');
    ev.push(`[+2] Hip markedly anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm > ${hipForwardCmMin} cm)`);
  } else if (hipFwdCm > strictHip) {
    pts += 1;
    activeSignals.push('sb.hipFwd.mildAnterior');
    ev.push(`[+1] Hip anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm > ${strictHip} cm)`);
  }

  if (torsoOffsetCm < -trunkLeanLatDegMin) {
    pts += 2;
    activeSignals.push('sb.torso.posteriorShouldersStrong');
    ev.push(`[+2] Shoulders posterior to hips vs plumb ("C" curve), Δ ≈ ${torsoOffsetCm.toFixed(1)} cm`);
  } else if (torsoOffsetCm < -strictLean) {
    pts += 1;
    activeSignals.push('sb.torso.posteriorShouldersMild');
    ev.push(`[+1] Mild posterior shoulder shift vs hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm)`);
  }

  if (isFinite(pelvicTilt) && pelvicTilt <= -strictTilt) {
    pts += 1;
    activeSignals.push('sb.pelvis.posteriorTilt');
    ev.push(`[+1] Pelvic line suggests posterior tilt (~${pelvicTilt.toFixed(1)}°)`);
  }

  // --- Knee-flexion → Swayback guard ---
  // When pelvic tilt is anterior (≥ sex-resolved threshold), knee flexion is more likely
  // compensatory posterior pelvic compression (APT variant) than a primary swayback strategy.
  // In this case we suppress the knee-flexion and knee-hip-anterior Swayback signals.
  const pelvicTiltIsAnterior =
    typeof pelvicTiltDegMin === 'number' &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= pelvicTiltDegMin;

  if (kneeFlex && !pelvicTiltIsAnterior) {
    pts += 2;
    activeSignals.push('sb.knee.flexion');
    const deg =
      typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg)
        ? kneeFlexEvidenceDeg.toFixed(0)
        : null;
    ev.push(
      deg
        ? `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}° (~${deg}°; typical swayback compensation vs locked knees)`
        : `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}° (typical swayback compensation vs locked knees)`
    );
  } else if (kneeFlex && pelvicTiltIsAnterior) {
    activeSignals.push('sb.knee.flexion.suppressedByAnteriorPelvicTilt');
    const deg =
      typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg)
        ? kneeFlexEvidenceDeg.toFixed(0)
        : null;
    ev.push(
      deg
        ? `[+0] Knee flexion (~${deg}°) suppressed as Swayback signal: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°) → likely compensatory APT, not primary swayback`
        : `[+0] Knee flexion suppressed as Swayback signal: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°) → likely compensatory APT, not primary swayback`
    );
  }

  const kneeHipSbMin = rules.kneeHipAnteriorSwaybackCmMin;
  if (
    typeof kneeHipSbMin === 'number' &&
    typeof kneeHipAntCm === 'number' &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm > kneeHipSbMin &&
    !pelvicTiltIsAnterior
  ) {
    pts += 1;
    activeSignals.push('sb.kneeHip.anterior');
    ev.push(`[+1] Knees anterior to hips (${kneeHipAntCm.toFixed(1)} cm > ${kneeHipSbMin} cm; flexed-knee braking)`);
  }

  return { pts: Math.min(MAX_ORIENTATION_POINTS, pts), evidence: ev, activeSignals };
}

export function scoreApt(
  hipFwdCm: number,
  torsoOffsetCm: number,
  kneeHyperext: boolean,
  pelvicTilt: number,
  rules: any,
  clientSex: 'Male' | 'Female' | 'Unknown',
  kneeHipAntCm?: number
): OrientationScore {
  const ev: string[] = [];
  const activeSignals: string[] = [];
  let pts = 0;
  const pelvicTiltDegMin =
    clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;
  const { trunkLeanLatDegMin, strictLean, hipForwardCmMin } = rules;

  if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) {
    pts += 2;
    activeSignals.push('apt.pelvis.anteriorTilt');
    ev.push(`[+2] Anterior pelvic orientation (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°)`);
  }

  if (torsoOffsetCm > trunkLeanLatDegMin) {
    pts += 2;
    activeSignals.push('apt.torso.anteriorShouldersStrong');
    ev.push(`[+2] Shoulders anterior to hips vs plumb (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm)`);
  } else if (torsoOffsetCm > strictLean) {
    pts += 1;
    activeSignals.push('apt.torso.anteriorShouldersMild');
    ev.push(`[+1] Mild forward trunk vs hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm)`);
  }

  if (kneeHyperext) {
    pts += 2;
    activeSignals.push('apt.knee.hyperextension');
    ev.push('[+2] Knee hyperextension pattern (primary compensatory sign with anterior pelvic orientation)');
  }

  if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin && hipFwdCm < hipForwardCmMin) {
    pts += 1;
    activeSignals.push('apt.hip.notMarkedAnterior');
    ev.push(`[+1] Hip stack not far forward of ankle (${hipFwdCm.toFixed(1)} cm < ${hipForwardCmMin} cm)`);
  }

  const kneeHipAptMin = rules.kneeHipPosteriorAptCmMin;
  if (
    typeof kneeHipAptMin === 'number' &&
    typeof kneeHipAntCm === 'number' &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm < -kneeHipAptMin
  ) {
    pts += 1;
    activeSignals.push('apt.kneeHip.posterior');
    ev.push(`[+1] Knees posterior to hips (${kneeHipAntCm.toFixed(1)} cm < -${kneeHipAptMin} cm; locked-knee strategy)`);
  }

  return { pts: Math.min(MAX_ORIENTATION_POINTS, pts), evidence: ev, activeSignals };
}

export const hipDisplacementHorizontalStrategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: MAX_ORIENTATION_POINTS,

  classify(
    input: PostureClassificationStrategyInput,
    context: PostureClassificationStrategyContext
  ): PostureClassificationStrategyResult | null {
    const rules = (getEvaluationRules() as any).posture.thresholds;
    const kneeThresholds = {
      kneeFlexionDegMin: rules.kneeFlexionDegMin,
      kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
    };
    const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
    if (!parsed) return null;

    const fromLm = sagittalFromLandmarks(
      input,
      context,
      kneeThresholds.kneeFlexionDegMin,
      kneeThresholds.kneeHyperextensionDegMin
    );
    const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
    const shoFwdCm = fromLm?.shoFwdCm ?? parsed.shoFwdVal;
    const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
    const kneeHyperext = fromLm
      ? fromLm.kneeHyperext
      : parsed.isKneeHyperext && !parsed.isKneeNeutral;
    const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
    const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;

    const pelvicTiltDegMin =
      input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;

    const kneeFlexEvidenceDeg =
      fromLm?.kneeFlexEvidenceDeg ??
      (!fromLm && kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : undefined);

    const kneeHipAntCm =
      fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

    logPostureClassificationDebug(
      DEBUG_LOG_PREFIX,
      'classification inputs (UI-mapped)',
      buildOrientationUiMappedInputs({
        analysisData: input.analysisData,
        parsedViewSide: parsed.viewSide,
        clientSex: input.clientSex,
        fromLandmarks: !!fromLm,
        hipAnteriorShiftCm: hipFwdCm,
        shoulderAnteriorShiftCm: shoFwdCm,
        shoulderMinusHipOffsetCm: torsoOffsetCm,
        kneeHyperextension: kneeHyperext,
        kneeFlexion: fromLm?.kneeFlex ?? (parsed.isKneeFlexed ? true : null),
        pelvicTiltDeg: pelvicTilt,
        parsed,
        pelvicTiltDegMin,
        rules,
        kneeHyperextensionDegMin: kneeThresholds.kneeHyperextensionDegMin,
        hasAsisPsisLine: !!context.asisPsisLine,
        frontalViewForContext: context.frontalView,
      })
    );

    const sb = scoreSwayback(
      hipFwdCm,
      torsoOffsetCm,
      kneeFlex,
      pelvicTilt,
      rules,
      kneeFlexEvidenceDeg,
      kneeHipAntCm,
      pelvicTiltDegMin
    );
    const apt = scoreApt(
      hipFwdCm,
      torsoOffsetCm,
      kneeHyperext,
      pelvicTilt,
      rules,
      input.clientSex,
      kneeHipAntCm
    );

    let orientation: PostureOrientationLabel = 'Stacked';
    let matched = 0;
    let evidence: string[] = [];
    let decisionPath = '';

    if (sb.pts === 0 && apt.pts === 0) {
      orientation = 'Stacked';
      evidence = ['No strong swayback vs anterior-orientation separation on current landmarks/metrics.'];
      matched = 0;
      decisionPath = 'noPoints.stacked';
    } else if (sb.pts > apt.pts) {
      orientation = 'Swayback';
      matched = sb.pts;
      evidence = sb.evidence;
      decisionPath = 'sbPointsGreater';
    } else if (apt.pts > sb.pts) {
      orientation = 'Anterior Pelvic Tilt';
      matched = apt.pts;
      evidence = apt.evidence;
      decisionPath = 'aptPointsGreater';
    } else {
      if (hipFwdCm >= rules.hipForwardCmMin && sb.pts > 0) {
        orientation = 'Swayback';
        matched = sb.pts;
        evidence = [...sb.evidence, 'Tie-break: large hip–ankle offset favors swayback shift.'];
        decisionPath = 'tieBreak.hipFwdFavorsSwayback';
      } else {
        orientation = 'Anterior Pelvic Tilt';
        matched = apt.pts;
        evidence = [...apt.evidence, 'Tie-break: hip stack not dominant → anterior orientation.'];
        decisionPath = 'tieBreak.hipStackFavorsApt';
      }
    }

    logOrientationDecisionDebug(DEBUG_LOG_PREFIX, {
      signalUiMap: HIP_DISPLACEMENT_SIGNAL_UI_MAP,
      activeSignals: [...sb.activeSignals, ...apt.activeSignals],
      signalGroups: [
        { label: 'swayback', prefix: 'sb.', possible: HIP_SWAYBACK_POSSIBLE_SIGNALS },
        { label: 'apt', prefix: 'apt.', possible: HIP_APT_POSSIBLE_SIGNALS },
      ],
      scoreByGroup: { swayback: { pts: sb.pts }, apt: { pts: apt.pts } },
      decisionPath,
      orientation,
      matchedPoints: matched,
      maxPoints: MAX_ORIENTATION_POINTS,
      tieInputs:
        sb.pts === apt.pts && sb.pts > 0
          ? {
              'Left/Right View Report — Hip Anterior Shift (cm)': hipFwdCm,
              'threshold.hipForwardCmMin (cm)': rules.hipForwardCmMin,
              hipAnteriorShiftMeetsSwaybackMin: hipFwdCm >= rules.hipForwardCmMin,
            }
          : null,
      finalEvidence: evidence,
    });

    const confidencePct =
      orientation === 'Stacked'
        ? Math.min(
            100,
            Math.round(
              (1 - Math.max(sb.pts, apt.pts) / Math.max(1, MAX_ORIENTATION_POINTS * 2)) * 100
            )
          )
        : Math.min(100, Math.round((matched / MAX_ORIENTATION_POINTS) * 100));

    return {
      orientation,
      confidencePct,
      matchedPoints: matched,
      maxPoints: MAX_ORIENTATION_POINTS,
      strategyId: STRATEGY_ID,
      strategyDisplayName: STRATEGY_NAME,
      evidence,
    };
  },
};
